import { environment } from '../../../environments/environment';
import { isInstalledAppMode } from '../analytics/device-context';
import { browserFamily, type BrowserVersionNavigator } from './browser-version-gate';

/** `environment.autoLaunchSetup`: per-environment kill switch + re-offer marker. */
export interface StartupHelperConfig {
  enabled: boolean;
  requiredVersion: string;
}

/**
 * The JioPC Launcher (source: tools/startup-launcher/, docs/launcher-integration-plan.md):
 * registers a per-user Task Scheduler logon task (HKCU `Run` fallback, never a UAC prompt),
 * then at each Windows sign-in shows a JioPC splash and opens the installed PWA behind it via
 * its own Chrome/Edge shortcut. Shipped from `public/downloads/` and served by nginx's
 * `/downloads/` block, which forces a download and 404s instead of falling back to index.html.
 * It replaced the Windows App team's Run-key helper (JioPCStartup.exe).
 *
 * A web page cannot run or install it — the user downloads and runs it themselves.
 * Signing only removes the SmartScreen warning; it does not make the install silent.
 * On success it opens `<scheme>://setup-complete`, and every sign-in launch carries
 * `?launcher=1`; StartupHelperLaunchService turns either into the persisted "set up" state —
 * the only ways the PWA learns the user actually ran it.
 *
 * One name everywhere: the download, what users are told to open, the launcher's own build
 * output, and its installed copy (`%LOCALAPPDATA%\JioPC\Launcher\JioPCLauncher.exe`).
 */
export const STARTUP_HELPER_FILE_NAME = 'JioPCLauncher.exe';
export const STARTUP_HELPER_URL = `/downloads/${STARTUP_HELPER_FILE_NAME}`;

type StartupHelperNavigator = BrowserVersionNavigator & {
  userAgentData?: BrowserVersionNavigator['userAgentData'] & { platform?: string };
};

function isWindows(nav: StartupHelperNavigator): boolean {
  const platform = nav.userAgentData?.platform;
  return platform ? platform === 'Windows' : /Windows/i.test(nav.userAgent ?? '');
}

const currentNavigator = (): StartupHelperNavigator =>
  typeof navigator !== 'undefined' ? navigator : {};

/**
 * Whether this device is one the helper can work on at all: switched on for this environment,
 * Windows, Chrome or Edge. Independent of install state, so surfaces that run before install
 * (the install screen's run-on-login checkbox) and the browser-toggle guidance can use it to
 * step aside for the helper — Windows Chrome/Edge gets the exe, everything else keeps the
 * guidance.
 *
 * Chrome and Edge: the launcher reuses whichever browser's PWA shortcut it finds
 * (`chrome_proxy.exe` / `msedge_proxy.exe`).
 */
export function isStartupHelperSupported(
  nav: StartupHelperNavigator = currentNavigator(),
  config: StartupHelperConfig = environment.autoLaunchSetup,
): boolean {
  const family = browserFamily(nav);
  return config.enabled && isWindows(nav) && (family === 'chrome' || family === 'edge');
}

/**
 * Offer the helper only where it can work: a supported device, in the installed app (the exe
 * registers the installed app's shortcut — before install there is nothing for it to find).
 */
export function canOfferStartupHelper(
  nav: StartupHelperNavigator = currentNavigator(),
  installed: boolean = isInstalledAppMode(),
  config: StartupHelperConfig = environment.autoLaunchSetup,
): boolean {
  return installed && isStartupHelperSupported(nav, config);
}

/** Days a "Not now" on the Login/Dashboard auto-start card stays quiet before asking again. */
export const STARTUP_HELPER_NOT_NOW_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Persisted "downloaded" marker, tied to `requiredVersion` so bumping that in the environment
 * re-offers the helper to everyone who already downloaded an older one (a replaced exe, or a
 * deliberate "ask everyone again").
 */
export function startupHelperDownloadedMarker(
  requiredVersion: string = environment.autoLaunchSetup.requiredVersion,
): string {
  return `downloaded:${requiredVersion}`;
}

/**
 * Persisted "set up" marker: the helper reported success through its `setup-complete` link
 * (see {@link parseStartupHelperSetupLink}). Stronger than "downloaded" — a download only means
 * the file reached the Downloads folder, not that the user ran it — so every surface treats it
 * as done (StartupHelperStateService). Same `requiredVersion` tie as the downloaded marker.
 */
export function startupHelperSetUpMarker(
  requiredVersion: string = environment.autoLaunchSetup.requiredVersion,
): string {
  return `setup:${requiredVersion}`;
}

/** Whether the stored state says the helper is set up for the current `requiredVersion`. */
export function isStartupHelperSetUp(
  stored: string | null,
  requiredVersion: string = environment.autoLaunchSetup.requiredVersion,
): boolean {
  return stored === startupHelperSetUpMarker(requiredVersion);
}

/**
 * Whether to show the auto-start card, given the stored state: the set-up or downloaded marker
 * for the current `requiredVersion`, or the epoch-ms of the last "Not now". An older marker or a
 * corrupt value parses as NaN, so it re-asks.
 */
export function shouldPromptStartupHelper(
  stored: string | null,
  now: number = Date.now(),
  requiredVersion: string = environment.autoLaunchSetup.requiredVersion,
): boolean {
  if (stored === null) return true;
  if (isStartupHelperSetUp(stored, requiredVersion)) return false;
  if (stored === startupHelperDownloadedMarker(requiredVersion)) return false;
  const at = Number(stored);
  return !Number.isFinite(at) || now - at >= STARTUP_HELPER_NOT_NOW_COOLDOWN_MS;
}

/**
 * Query param the manifests' `protocol_handlers` put the protocol URL in (`url: "/?launch=%s"`),
 * URL-encoded — e.g. `?launch=web%2Bjiopc%3A%2F%2Fsetup-complete%3Fmethod%3Dtask`.
 */
export const PROTOCOL_LAUNCH_PARAM = 'launch';

/**
 * The protocol URL carried by an app URL's `launch` param, or null when it has none. Accepts a
 * relative URL (`/?launch=…`) too. `launchQueue` hands over the handler URL, not the bare
 * protocol URL, so the warm path needs this as much as the cold one.
 */
export function protocolLaunchLink(appUrl: string, base = 'https://app.invalid'): string | null {
  try {
    return new URL(appUrl, base).searchParams.get(PROTOCOL_LAUNCH_PARAM);
  } catch {
    return null;
  }
}

/**
 * Query param the launcher adds to the URL it opens the app with at Windows sign-in
 * (`/?launcher=1`, tools/startup-launcher LaunchConfig.LaunchArguments). Seeing it proves the
 * launcher is installed and working, so it also counts as "set up" (StartupHelperLaunchService).
 */
export const LAUNCHER_LAUNCH_PARAM = 'launcher';

/** True when this page load was opened by the launcher (`?launcher=1`). */
export function isLauncherLaunch(appUrl: string, base = 'https://app.invalid'): boolean {
  try {
    return new URL(appUrl, base).searchParams.get(LAUNCHER_LAUNCH_PARAM) === '1';
  } catch {
    return false;
  }
}

/** How the helper registered the app: a Task Scheduler task or an HKCU `Run` entry. */
export type StartupHelperSetupMethod = 'task' | 'run';

/**
 * The helper's success link, `<scheme>://setup-complete[?method=task|run]` (brief:
 * docs/windows-helper-faster-startup-request.md §3.4). Only this environment's scheme counts —
 * every env registers its own (`environment.pwaLaunchProtocol`), so a dev helper can't mark a
 * prod install. Any other host/path is ignored (null). A trailing `/` is tolerated because the
 * Windows shell may add one; an unknown `method` still counts as success, with `method: null`.
 */
export function parseStartupHelperSetupLink(
  link: string,
  scheme: string = environment.pwaLaunchProtocol,
): { method: StartupHelperSetupMethod | null } | null {
  let url: URL;
  try {
    url = new URL(link.trim());
  } catch {
    return null;
  }
  if (url.protocol !== `${scheme.toLowerCase()}:`) return null;
  if (url.host.toLowerCase() !== 'setup-complete') return null;
  if (url.pathname !== '' && url.pathname !== '/') return null;
  const method = url.searchParams.get('method');
  return { method: method === 'task' || method === 'run' ? method : null };
}

/**
 * Starts the exe download from a click handler — always a user gesture, never scripted: corporate
 * proxies (Zscaler) hold or block an .exe fetched without one. The browser saves it to the user's
 * download folder — a page cannot pick the path, and the launcher does not need one: it copies
 * itself to `%LOCALAPPDATA%` and finds the app through the browser's shortcut.
 */
export function downloadStartupHelper(): void {
  const link = document.createElement('a');
  link.href = STARTUP_HELPER_URL;
  link.download = STARTUP_HELPER_FILE_NAME;
  link.click();
}
